import { createSlice, PayloadAction, createListenerMiddleware } from "@reduxjs/toolkit";
import { RootState } from ".";

export type ThemeOption = "light" | "dark";

interface ThemeState {
  theme: ThemeOption;
  isRespectingOS: boolean;
}

const initialState: ThemeState = {
  theme: "light",
  isRespectingOS: false,
};

export const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeOption>) => {
      state.theme = action.payload;
      state.isRespectingOS = false;
    },

    respectOSTheme: (state) => {
      state.isRespectingOS = true;
      const osPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      console.log("osPrefersDark", osPrefersDark);
      state.theme = osPrefersDark ? "dark" : "light";
    },

    initializeTheme: (state) => {
      const localTheme = localStorage.getItem("theme");
      const osPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const theme = localTheme === "dark" || (!localTheme && osPrefersDark) ? "dark" : "light";

      state.theme = theme;
      state.isRespectingOS = !localTheme;
    },
  },
});

export const { setTheme, respectOSTheme, initializeTheme } = themeSlice.actions;

// Create listener middleware for side effects
export const themeListenerMiddleware = createListenerMiddleware();

// Listen for theme changes and update DOM/localStorage
themeListenerMiddleware.startListening({
  actionCreator: setTheme,
  effect: (action) => {
    const theme = action.payload;

    // Update localStorage
    localStorage.setItem("theme", theme);

    // Update DOM
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  },
});

// Listen for respectOS changes
themeListenerMiddleware.startListening({
  actionCreator: respectOSTheme,
  effect: (action, { getState }) => {
    const state = getState() as RootState;
    const theme = state.theme.theme;

    // Remove from localStorage to respect OS
    localStorage.removeItem("theme");

    // Update DOM
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  },
});

// Listen for initialization
themeListenerMiddleware.startListening({
  actionCreator: initializeTheme,
  effect: (_, listenerApi) => {
    const state = listenerApi.getState() as { theme: ThemeState };
    const theme = state.theme.theme;

    // Update DOM based on initialized theme
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  },
});
export const themeReducer = themeSlice.reducer;
